import { useTranslation } from 'react-i18next';
import { useMobileShell } from '@/hooks/useMobileShell';
import { cn } from '@/lib/cn';

export interface StatusTab<T extends string> {
  /** `null` 代表「全部记录」 */
  value: T | null;
  label: string;
  /** 页签上的数字。`undefined` 时不显示（不带数的页签，或还没加载出来） */
  count?: number;
  /** 等人处理的页签：有数时数字用醒目色，和工地概览「待处理」一个颜色 */
  attention?: boolean;
}

/**
 * 按状态分的页签（CR-050）。
 *
 * 客户原话「又是日期，又是全部，会迷糊了」。成熟的账单 / 报销页（Xero、QuickBooks、Zoho Expense、Ramp）
 * 都把状态做成**页签**：它是这一页的主导航，用动作命名，等人处理的带数量。
 *
 * **和 `StatusChips` 故意长得不一样**：「本工地 / 我的」那排还是按钮，状态用下划线页签，
 * 两排叠在一起时一眼分得出哪个是范围、哪个是状态。
 *
 * 手机上一行放不下时**只让页签条自己横向滑动**，页面不滑。
 *
 * CR-082：电脑上是下划线页签；手机上是一排胶囊（选中的实心），和 App 里其它胶囊一个样子。
 * 数字：等人处理的有数时红底白字（和侧栏菜单上的待处理数同一个颜色），其余灰底。
 */
export function StatusTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: ReadonlyArray<StatusTab<T>>;
  value: T | null;
  onChange: (next: T | null) => void;
  /** 读屏念的这排页签是什么。不给就是「按状态筛选」—— 拿它当别的页签用时（用户编辑抽屉，CR-078）要换 */
  label?: string;
}) {
  const { t } = useTranslation();
  const mobile = useMobileShell();

  const count = (tab: StatusTab<T>, selected: boolean) =>
    tab.count !== undefined && (
      <span
        className={cn(
          'rounded-full px-1.5 text-micro font-semibold leading-[18px] tabular-nums',
          tab.attention && tab.count > 0
            ? 'bg-danger-solid text-white'
            : mobile && selected
              ? 'bg-white/20 text-current'
              : 'bg-surface-2 text-ink-3',
        )}
      >
        {tab.count}
      </span>
    );

  if (mobile) {
    return (
      <div className="-mx-4 mb-3 overflow-x-auto px-4 [scrollbar-width:none]">
        <div role="tablist" aria-label={label ?? t('filters.status')} className="flex min-w-max gap-2">
          {tabs.map((tab) => {
            const selected = tab.value === value;
            return (
              <button
                key={tab.value ?? '__all__'}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => onChange(tab.value)}
                className={cn(
                  'flex min-h-touch-sm items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-body font-medium transition-colors',
                  selected ? 'border-ink bg-ink text-surface' : 'border-rule bg-surface text-ink-2',
                )}
              >
                {tab.label}
                {count(tab, selected)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-1 overflow-x-auto overflow-y-hidden border-b border-rule">
      <div role="tablist" aria-label={label ?? t('filters.status')} className="flex min-w-max gap-5">
        {tabs.map((tab) => {
          const selected = tab.value === value;
          return (
            <button
              key={tab.value ?? '__all__'}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(tab.value)}
              className={cn(
                'relative flex min-h-touch items-center gap-1.5 whitespace-nowrap text-body font-medium transition-colors',
                'after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full',
                // 选中是正文色 + 正文色下划线，不用主色 —— 主色留给按钮和链接（CR-081）
                selected ? 'text-ink after:bg-ink' : 'text-ink-3 after:bg-transparent hover:text-ink',
              )}
            >
              {tab.label}
              {count(tab, selected)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
